import { useCallback, useEffect, useMemo, useState } from "react";
import ThemeToggle from "./ThemeToggle";
import ThemeChoiceDialog from "./ThemeChoiceDialog";
import { Link, NavLink, Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { FarmaciaAuthProvider, useFarmaciaAuth } from "../context/FarmaciaAuthContext";
import {
  BarChart3,
  BookOpen,
  Building2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ClipboardCheck,
  FileUp,
  Headphones,
  LayoutDashboard,
  Loader2,
  LogOut,
  Menu,
  Package,
  Receipt,
  Settings,
  Truck,
  Users,
  Bell,
  Warehouse,
  X,
  MessageCircle,
} from "lucide-react";
import { Button } from "./ui/button";
import FarmaciaAssistantChatWidget from "./FarmaciaAssistantChatWidget";
import {
  FarmaciaChromeProvider,
  FarmaciaHeaderActions,
  FarmaciaHeaderTitle,
} from "./FarmaciaPageChrome";
import { farmGet, farmPost } from "../pages/farmacia/shared";

const LOGO_FULL_URL = "/images/logo-pharma.png";
const LOGO_MARK_URL = "/images/logo-pharma-mark.png";

/** Voci flat + sezioni (IA mockup). Figli sotto Pratiche / Magazzino / Amministrazione. */
const NAV_SECTIONS = [
  {
    title: "Operatività",
    items: [
      {
        to: "/farmacia",
        end: true,
        label: "Dashboard",
        icon: LayoutDashboard,
        permission: "dashboard:read",
        feature: "dashboard_v1",
      },
      {
        to: "/farmacia/strutture",
        label: "Strutture",
        icon: Building2,
        permission: "dashboard:read",
        feature: "dashboard_v1",
      },
      {
        to: "/farmacia/ospiti",
        label: "Ospiti",
        icon: Users,
      },
      {
        id: "pratiche",
        label: "Pratiche",
        icon: ClipboardCheck,
        to: "/farmacia/pratiche",
        children: [
          {
            to: "/farmacia/pratiche",
            end: true,
            label: "Flusso pratiche",
            icon: ClipboardCheck,
          },
          {
            to: "/farmacia/richieste",
            label: "Richieste Carepy",
            icon: FileUp,
            permission: "carepy:write",
            feature: "carepy_import",
          },
        ],
      },
      {
        to: "/farmacia/ordini",
        label: "Ordini",
        icon: Package,
        permission: "dashboard:read",
        feature: "ordini_extra",
      },
      {
        to: "/farmacia/consegne",
        label: "Consegne",
        icon: Truck,
        permission: "consegne:read",
        feature: "consegne_v1",
      },
      {
        to: "/farmacia/autista",
        label: "Vista autista",
        icon: Truck,
        permission: "consegne:autista",
        feature: "consegne_v1",
      },
    ],
  },
  {
    title: "Gestione",
    items: [
      {
        id: "magazzino",
        label: "Magazzino",
        icon: Warehouse,
        to: "/farmacia/magazzino",
        children: [
          {
            to: "/farmacia/magazzino",
            end: true,
            label: "Panoramica",
            icon: Warehouse,
          },
          {
            to: "/farmacia/scorte",
            label: "Scorte",
            icon: Package,
            permission: "scorte:read",
            feature: "scorte_v1",
          },
          {
            to: "/farmacia/catalogo",
            label: "Catalogo",
            icon: BookOpen,
            permission: "catalogo:read",
            feature: "catalogo_import",
          },
        ],
      },
      {
        id: "amministrazione",
        label: "Amministrazione",
        icon: Receipt,
        to: "/farmacia/amministrazione",
        children: [
          {
            to: "/farmacia/amministrazione",
            end: true,
            label: "Panoramica",
            icon: Receipt,
          },
          {
            to: "/farmacia/fatturazione",
            label: "Fatturazione",
            icon: Receipt,
            permission: "fatture:read",
            feature: "fatture_v1",
          },
          {
            to: "/farmacia/clienti",
            label: "Clienti diretti",
            icon: Users,
            permission: "clienti:read",
          },
        ],
      },
      {
        to: "/farmacia/report",
        label: "Report",
        icon: BarChart3,
      },
    ],
  },
  {
    title: "Sistema",
    items: [
      {
        to: "/farmacia/impostazioni",
        label: "Impostazioni",
        icon: Settings,
        permission: "config:manage",
      },
    ],
  },
];

function formatDateLong(d = new Date()) {
  return d.toLocaleDateString("it-IT", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "Buongiorno";
  if (h < 18) return "Buon pomeriggio";
  return "Buonasera";
}

function FarmaciaHeaderGreeting({ farmaciaLabel }) {
  return (
    <div className="min-w-0">
      <p className="truncate text-xl font-semibold leading-tight tracking-tight text-slate-900 md:text-2xl">
        {greeting()}, {farmaciaLabel}
      </p>
      <p className="mt-0.5 truncate capitalize text-sm text-slate-500">{formatDateLong()}</p>
    </div>
  );
}

function canSeeNav(user, item) {
  if (item.hidden) return false;
  const features = user?.config?.features || {};
  if (item.feature && features[item.feature] === false) return false;
  if (!user?.utenti_ruoli_v1) {
    if (item.to === "/farmacia/autista") return false;
    return true;
  }
  const perms = new Set(user?.permessi || []);
  if (item.permission && !perms.has(item.permission)) return false;
  if (user?.ruolo === "autista") {
    return item.to === "/farmacia/autista";
  }
  return true;
}

function filterNavSections(user) {
  return NAV_SECTIONS.map((section) => ({
    ...section,
    items: section.items
      .map((item) => {
        if (item.children) {
          const children = item.children.filter((c) => canSeeNav(user, c));
          if (!children.length) return null;
          return { ...item, children };
        }
        return canSeeNav(user, item) ? item : null;
      })
      .filter(Boolean),
  })).filter((s) => s.items.length > 0);
}

export function FarmaciaRoot() {
  return (
    <FarmaciaAuthProvider>
      <Outlet />
    </FarmaciaAuthProvider>
  );
}

function getInitials(nome = "") {
  const parts = nome.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "F";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
}

function FarmaciaNotificheBell() {
  const { token, user } = useFarmaciaAuth();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);

  const enabled = user?.config?.features?.notifiche_v1 !== false;

  const load = useCallback(async () => {
    if (!token || !enabled) return;
    try {
      const res = await farmGet("/farmacia/notifiche", token, { limit: 30 });
      setItems(res.items || []);
      setUnread(res.unread || 0);
    } catch {
      /* ignore */
    }
  }, [token, enabled]);

  useEffect(() => {
    load();
    if (!enabled) return undefined;
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, [load, enabled]);

  if (!enabled) return null;

  const markAll = async () => {
    try {
      await farmPost("/farmacia/notifiche/letta-tutte", token, {});
      await load();
    } catch {
      /* ignore */
    }
  };

  const openOne = async (n) => {
    try {
      if (!n.read) await farmPost(`/farmacia/notifiche/${encodeURIComponent(n.id)}/letta`, token, {});
    } catch {
      /* ignore */
    }
    setOpen(false);
    if (n.path) window.location.href = n.path.startsWith("/") ? n.path : `/farmacia`;
  };

  return (
    <div className="relative">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="relative h-10 w-10 rounded-xl p-0"
        onClick={() => {
          setOpen((v) => !v);
          load();
        }}
        aria-label="Notifiche"
      >
        <Bell className="h-4.5 w-4.5 h-[18px] w-[18px]" />
        {unread > 0 ? (
          <span className="absolute right-1.5 top-1.5 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-semibold text-white">
            {unread > 9 ? "9+" : unread}
          </span>
        ) : null}
      </Button>
      {open ? (
        <div className="absolute right-0 z-50 mt-1 w-80 rounded-xl border border-slate-200 bg-white shadow-lg">
          <div className="flex items-center justify-between border-b px-3 py-2">
            <span className="text-sm font-semibold text-slate-800">Notifiche</span>
            <button type="button" className="text-xs text-emerald-700" onClick={markAll}>
              Segna tutte lette
            </button>
          </div>
          <ul className="max-h-80 overflow-y-auto">
            {items.length === 0 ? (
              <li className="px-3 py-6 text-center text-xs text-slate-400">Nessuna notifica</li>
            ) : (
              items.map((n) => (
                <li key={n.id}>
                  <button
                    type="button"
                    className={`w-full px-3 py-2 text-left hover:bg-slate-50 ${n.read ? "" : "bg-emerald-50/40"}`}
                    onClick={() => openOne(n)}
                  >
                    <div className="text-xs font-semibold text-slate-800">{n.titolo}</div>
                    <div className="text-[11px] text-slate-500">{n.messaggio}</div>
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

function NavItemLink({ item, onNavigate, collapsedChild, collapsed = false }) {
  const location = useLocation();
  const navigate = useNavigate();
  const Icon = item.icon;

  const isHashItem = Boolean(item.hash);
  const dashHash = location.pathname === "/farmacia" ? location.hash : "";
  const hashActive = isHashItem && location.pathname === "/farmacia" && dashHash === `#${item.hash}`;
  // Dashboard non resta attiva se si è su #ordini
  const dashConflict =
    !isHashItem &&
    item.to === "/farmacia" &&
    item.end &&
    dashHash === "#ordini";

  const handleClick = (e) => {
    if (isHashItem) {
      e.preventDefault();
      navigate(`/farmacia#${item.hash}`);
      setTimeout(() => {
        document.getElementById(item.hash)?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 50);
      onNavigate?.();
      return;
    }
    if (item.to === "/farmacia" && item.end) {
      e.preventDefault();
      navigate("/farmacia");
      document.querySelector("main.page-transition")?.scrollTo({ top: 0, behavior: "smooth" });
    }
    onNavigate?.();
  };

  const base = collapsedChild
    ? "mb-0.5 ml-3 flex items-center gap-2.5 rounded-xl px-3 py-1.5 text-xs font-medium transition-all"
    : "mb-0.5 flex w-full items-center gap-3 rounded-xl px-3 py-2 text-sm font-medium transition-all";
  const collapsedBase = "mb-0.5 flex h-10 w-full items-center justify-center rounded-xl transition-all";

  return (
    <NavLink
      to={isHashItem ? `/farmacia#${item.hash}` : item.to}
      end={item.end}
      onClick={handleClick}
      title={collapsed ? item.label : undefined}
      className={({ isActive }) => {
        const active = isHashItem ? hashActive : isActive && !dashConflict;
        return `${collapsed ? collapsedBase : base} ${
          active
            ? "bg-white text-[#0F5C4C] shadow-sm"
            : "text-slate-600 hover:bg-white/70 hover:text-slate-900"
        }`;
      }}
    >
      {({ isActive }) => {
        const active = isHashItem ? hashActive : isActive && !dashConflict;
        return (
          <>
            {Icon ? (
              <Icon
                className={`shrink-0 ${collapsedChild ? "h-3.5 w-3.5" : "h-[18px] w-[18px]"} ${
                  active ? "text-[#0F5C4C]" : "text-slate-400"
                }`}
              />
            ) : null}
            <span className={collapsed ? "sr-only" : "truncate"}>{item.label}</span>
          </>
        );
      }}
    </NavLink>
  );
}

function NavGroup({ item, onNavigate, collapsed = false }) {
  const location = useLocation();
  const navigate = useNavigate();
  const parentActive = Boolean(item.to && location.pathname === item.to);
  const childActive =
    parentActive ||
    item.children?.some((c) => location.pathname === c.to || location.pathname.startsWith(`${c.to}/`));
  const [open, setOpen] = useState(childActive);
  const Icon = item.icon;

  useEffect(() => {
    if (childActive) setOpen(true);
  }, [childActive]);

  return (
    <div className={`relative mb-0.5 ${collapsed ? "group" : ""}`}>
      <div
        className={`flex ${collapsed ? "h-10 w-full justify-center" : "w-full items-center gap-1"} rounded-xl transition-all ${
          childActive
            ? "bg-white text-[#0F5C4C] shadow-sm"
            : "text-slate-600 hover:bg-white/70 hover:text-slate-900"
        }`}
      >
        <button
          type="button"
          title={collapsed ? item.label : undefined}
          className={`flex min-w-0 flex-1 items-center gap-3 ${collapsed ? "justify-center p-2" : "px-3 py-2 text-left"} text-sm font-medium`}
          onClick={() => {
            if (item.to) {
              navigate(item.to);
              onNavigate?.();
            } else {
              setOpen((v) => !v);
            }
          }}
        >
          {Icon ? (
            <Icon
              className={`h-[18px] w-[18px] shrink-0 ${
                childActive ? "text-[#0F5C4C]" : "text-slate-400"
              }`}
            />
          ) : null}
          <span className={collapsed ? "sr-only" : "truncate"}>{item.label}</span>
        </button>
        {!collapsed ? (
          <button
            type="button"
            className="shrink-0 px-2 py-2"
            aria-label={open ? "Chiudi sottomenu" : "Apri sottomenu"}
            onClick={() => setOpen((v) => !v)}
          >
            <ChevronDown className={`h-3.5 w-3.5 text-slate-400 transition ${open ? "rotate-180" : ""}`} />
          </button>
        ) : null}
      </div>
      {open && !collapsed ? (
        <div className="mt-0.5 space-y-0.5">
          {item.children.map((child) => (
            <NavItemLink
              key={`${child.to}-${child.label}`}
              item={child}
              onNavigate={onNavigate}
              collapsedChild
            />
          ))}
        </div>
      ) : null}
      {collapsed ? (
        <div className="invisible absolute left-[calc(100%+0.5rem)] top-0 z-50 w-52 rounded-xl border border-slate-200 bg-white p-2 opacity-0 shadow-lg transition group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100">
          <p className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500">{item.label}</p>
          {item.children.map((child) => (
            <NavItemLink
              key={`${child.to}-${child.label}`}
              item={child}
              onNavigate={onNavigate}
              collapsedChild={false}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

function FarmaciaSidebarNav({ sections, onNavigate, collapsed = false }) {
  return (
    <nav className={`sidebar-scroll flex-1 overflow-y-auto ${collapsed ? "px-2 pt-5 pb-2" : "px-2.5 pt-3 pb-2"}`}>
      {sections.map((section, sectionIdx) => (
        <div key={section.title} className={sectionIdx > 0 ? "mt-4" : ""}>
          <div className={collapsed ? "sr-only" : "mb-1 px-3 text-[10px] font-semibold uppercase tracking-wider text-slate-500"}>
            {section.title}
          </div>
          {section.items.map((item) =>
            item.children ? (
              <NavGroup key={item.id || item.label} item={item} onNavigate={onNavigate} collapsed={collapsed} />
            ) : (
              <NavItemLink key={`${item.to}-${item.hash || item.label}`} item={item} onNavigate={onNavigate} collapsed={collapsed} />
            )
          )}
        </div>
      ))}
    </nav>
  );
}

function FarmaciaHelpCard() {
  return (
    <div className="mx-3 mb-3 shrink-0 rounded-xl border border-slate-200 bg-white px-4 py-4 text-center shadow-sm">
      <div className="mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-slate-800">
        <Headphones className="h-5 w-5 text-white" />
      </div>
      <p className="text-sm font-semibold text-slate-800">Hai bisogno di aiuto?</p>
      <button
        type="button"
        onClick={() => window.dispatchEvent(new CustomEvent("farmacia-assistant-open"))}
        className="mt-3 inline-flex h-8 w-full items-center justify-center gap-1.5 rounded-lg bg-teal-600 text-xs font-semibold text-white transition hover:bg-teal-700"
        data-testid="farmacia-assistant-help-card"
        aria-label="Apri assistente IA farmacia"
      >
        <MessageCircle className="h-3.5 w-3.5" />
        Assistente
      </button>
      <a
        href="/documents/GUIDA_PORTALE_FARMACIA.html"
        target="_blank"
        rel="noreferrer"
        className="mt-2 inline-flex h-8 w-full items-center justify-center gap-1.5 rounded-lg bg-[#0F5C4C] text-xs font-semibold text-white transition hover:bg-[#0c4a3e]"
      >
        <BookOpen className="h-3.5 w-3.5" />
        Guida
      </a>
    </div>
  );
}

function SidebarBrand({ collapsed }) {
  return (
    <div className={`shrink-0 border-b border-slate-200/80 pt-4 pb-3 ${collapsed ? "px-1.5" : "px-4"}`}>
      <div className="flex h-12 items-center justify-center">
        <img
          src={collapsed ? LOGO_MARK_URL : LOGO_FULL_URL}
          alt="Pharma by Affidya"
          className={collapsed ? "h-8 w-8 object-contain" : "h-12 w-auto max-w-full"}
        />
      </div>
    </div>
  );
}

function SidebarChrome({ sections, onNavigate, className = "", collapsed = false }) {
  return (
    <div className={`flex h-full min-h-0 flex-col ${className}`}>
      <SidebarBrand collapsed={collapsed} />
      <FarmaciaSidebarNav sections={sections} onNavigate={onNavigate} collapsed={collapsed} />
      {!collapsed ? <FarmaciaHelpCard /> : null}
    </div>
  );
}

export default function FarmaciaLayout() {
  const { isAuthenticated, loading, user, logout } = useFarmaciaAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => localStorage.getItem("farmacia-sidebar-collapsed") === "true"
  );

  const sections = useMemo(() => filterNavSections(user), [user]);
  const displayName = user?.utente?.nome || user?.nome || "Farmacia";
  const farmaciaName = user?.nome || "Portale Farmacia";
  const farmaciaLabel = user?.nome || "Farmacia";

  useEffect(() => {
    localStorage.setItem("farmacia-sidebar-collapsed", String(sidebarCollapsed));
  }, [sidebarCollapsed]);

  const toggleSidebar = () => setSidebarCollapsed((current) => !current);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#D9E0EA]">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/farmacia-login" replace />;
  }

  if (user?.utenti_ruoli_v1 && user?.ruolo === "autista") {
    return (
      <FarmaciaChromeProvider>
        <div className="min-h-screen bg-[#D9E0EA] p-3 text-slate-900 md:p-4">
      <ThemeChoiceDialog />
          <div className="flex h-[calc(100vh-1.5rem)] flex-col overflow-hidden rounded-2xl bg-white shadow-[0_1px_3px_rgba(15,23,42,0.06)] md:h-[calc(100vh-2rem)]">
            <header className="shrink-0 border-b border-slate-100">
              <div className="flex min-h-14 items-center gap-3 px-4 py-2 md:px-6">
                <div className="min-w-0 flex-1">
                  <FarmaciaHeaderTitle />
                </div>
                <FarmaciaHeaderActions />
                <div className="flex items-center gap-2">
                  <ThemeToggle />
                  <FarmaciaNotificheBell />
                  <Button type="button" variant="ghost" size="sm" onClick={logout} className="rounded-xl">
                    <LogOut className="mr-1.5 h-4 w-4" />
                    Esci
                  </Button>
                </div>
              </div>
            </header>
            <main className="page-transition min-h-0 flex-1 overflow-auto px-4 py-6 md:px-6 md:py-8">
              <Outlet />
            </main>
          </div>
        </div>
      </FarmaciaChromeProvider>
    );
  }

  return (
    <FarmaciaChromeProvider>
    <div className="min-h-screen bg-[#D9E0EA] p-3 text-slate-900 md:p-4">
      {mobileOpen ? (
        <div className="fixed inset-0 z-40 lg:hidden">
          <button
            type="button"
            className="absolute inset-0 bg-slate-900/40"
            aria-label="Chiudi menu"
            onClick={() => setMobileOpen(false)}
          />
          <aside className="absolute left-0 top-0 flex h-full w-72 max-w-[85vw] flex-col bg-[#EEF0F3] shadow-xl">
            <button
              type="button"
              className="absolute right-3 top-4 z-10 rounded-lg p-1 text-slate-500 hover:bg-white/80"
              onClick={() => setMobileOpen(false)}
              aria-label="Chiudi"
            >
              <X className="h-5 w-5" />
            </button>
            <SidebarChrome sections={sections} onNavigate={() => setMobileOpen(false)} />
          </aside>
        </div>
      ) : null}

      <div className="flex h-[calc(100vh-1.5rem)] gap-[9px] md:h-[calc(100vh-2rem)] md:gap-3">
        <aside className={`relative z-20 hidden shrink-0 flex-col transition-[width] duration-200 lg:flex ${sidebarCollapsed ? "w-[68px]" : "w-64"}`}>
          <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-tl-2xl rounded-bl-2xl rounded-tr-none rounded-br-none bg-[#EEF0F3] shadow-[0_2px_8px_rgba(15,23,42,0.08)]">
            <SidebarChrome sections={sections} collapsed={sidebarCollapsed} />
          </div>
          <div className="absolute right-0 top-[76px] z-30 flex h-9 w-9 translate-x-[12px] -translate-y-1/2 items-center justify-center">
            <span className="pointer-events-none absolute h-9 w-9 rounded-full bg-[#D9E0EA]" aria-hidden />
            <button
              type="button"
              onClick={toggleSidebar}
              className="relative flex h-6 w-6 items-center justify-center rounded-full border border-slate-300 bg-white text-slate-600 shadow-sm transition hover:border-[#0F5C4C] hover:bg-[#0F5C4C] hover:text-white"
              aria-label={sidebarCollapsed ? "Espandi menu" : "Riduci menu"}
              title={sidebarCollapsed ? "Espandi menu" : "Riduci menu"}
            >
              {sidebarCollapsed ? (
                <ChevronRight className="h-3.5 w-3.5" strokeWidth={2.5} />
              ) : (
                <ChevronLeft className="h-3.5 w-3.5" strokeWidth={2.5} />
              )}
            </button>
          </div>
        </aside>

        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-2xl lg:rounded-tl-none lg:rounded-bl-none bg-white shadow-[0_1px_3px_rgba(15,23,42,0.06)]">
          {user?.demoMode && (
            <div className="shrink-0 bg-teal-700 px-3 py-2 text-center text-xs font-medium text-white">
              Ambiente Demo: dati fittizi, nessun invio verso servizi esterni.
            </div>
          )}
          <header className="shrink-0 border-b border-slate-100">
            <div className="flex min-h-16 flex-wrap items-center gap-x-3 gap-y-2 px-2 py-2 md:px-3">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-10 w-10 rounded-xl p-0 lg:hidden"
                onClick={() => setMobileOpen(true)}
                aria-label="Apri menu"
              >
                <Menu className="h-5 w-5" />
              </Button>

              <div className="min-w-0 flex-1">
                <FarmaciaHeaderTitle greeting={<FarmaciaHeaderGreeting farmaciaLabel={farmaciaLabel} />} />
              </div>

              <FarmaciaHeaderActions />

              <div className="flex items-center gap-1 sm:gap-2">
                <ThemeToggle />
                <FarmaciaNotificheBell />
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setProfileOpen((v) => !v)}
                    className="flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-100 py-1.5 pl-1.5 pr-2.5 transition hover:bg-slate-200"
                  >
                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#4CAF50] text-[11px] font-semibold text-white">
                      {getInitials(displayName)}
                    </span>
                    <span className="hidden max-w-[160px] text-left sm:block">
                      <span className="block truncate text-xs font-semibold leading-tight text-slate-800">
                        {farmaciaName}
                      </span>
                      <span className="block truncate text-[11px] leading-tight text-slate-500">
                        {displayName}
                      </span>
                    </span>
                    <ChevronDown className="hidden h-3.5 w-3.5 text-slate-400 sm:block" />
                  </button>
                  {profileOpen ? (
                    <div className="absolute right-0 z-50 mt-1 w-52 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg">
                      <Link
                        to="/farmacia/impostazioni"
                        className="block px-3 py-2.5 text-sm text-slate-700 hover:bg-slate-50"
                        onClick={() => setProfileOpen(false)}
                      >
                        Impostazioni
                      </Link>
                      <button
                        type="button"
                        className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm text-slate-700 hover:bg-slate-50"
                        onClick={() => {
                          setProfileOpen(false);
                          logout();
                        }}
                      >
                        <LogOut className="h-3.5 w-3.5" />
                        Esci
                      </button>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          </header>

          <main className="page-transition min-h-0 flex-1 overflow-auto px-4 py-5 md:px-6 md:py-6 lg:px-8">
            <Outlet />
          </main>
        </div>
      </div>

      <FarmaciaAssistantChatWidget sidebarCollapsed={sidebarCollapsed} />
    </div>
    </FarmaciaChromeProvider>
  );
}
